import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { markFilesIndexStartedInCache } from '@/hooks/files/cache/fileCacheSync'
import { fileQueryKeys } from '@/hooks/files/types'
import {
  organizationFilesIndexStatusQueryKeys,
  organizationFilesQueryKeys,
} from '@/hooks/organization/types'
import { boostOrganizationFilesIndexPolling } from '@/hooks/organization/useOrganizationFilesIndexStatus'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'

interface UseCreateOrganizationFileFromWebsiteArgs {
  orgId: UUID | ''
}

export interface CreateOrganizationFileFromWebsiteRequest {
  url: string
  tag_ids?: UUID[]
  unrestricted: boolean
}

export interface CreateOrganizationFileFromWebsiteResponse {
  workflow_id: string
  file_id: UUID
}

const createOrganizationFileFromWebsite = async (
  orgId: UUID | '',
  req: CreateOrganizationFileFromWebsiteRequest,
  accessToken: string,
): Promise<CreateOrganizationFileFromWebsiteResponse> => {
  if (!orgId) {
    throw new Error('Organization ID is required.')
  }

  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/files/from-website`,
    {
      method: 'POST',
      body: JSON.stringify(req),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )

  if (!res.ok) {
    const json = await res.json().catch(() => null)

    throw new SweetspotError({
      name: 'UPLOAD_FILE_ERROR',
      message: typeof json?.detail === 'string' ? json.detail : res.statusText,
      cause: {
        status: res.status,
      },
    })
  }

  return res.json()
}

export const useCreateOrganizationFileFromWebsite = ({
  orgId,
}: UseCreateOrganizationFileFromWebsiteArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()
  const queryClient = useQueryClient()

  return useMutation<
    CreateOrganizationFileFromWebsiteResponse,
    SweetspotError,
    CreateOrganizationFileFromWebsiteRequest
  >({
    mutationFn: (req) =>
      createOrganizationFileFromWebsite(orgId, req, accessToken),
    onError: (err) => {
      handleError(err)
    },
    onSuccess: async (data) => {
      const filesKey = organizationFilesQueryKeys.organization({ orgId })
      const fileKey = fileQueryKeys.file({ fileId: data.file_id })
      boostOrganizationFilesIndexPolling({
        queryClient,
        orgId,
      })
      markFilesIndexStartedInCache({
        queryClient,
        fileIds: [data.file_id],
        orgId,
      })

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: filesKey }),
        queryClient.invalidateQueries({
          queryKey: organizationFilesIndexStatusQueryKeys.all(),
        }),
        queryClient.invalidateQueries({ queryKey: fileKey }),
      ])
    },
  })
}
